import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Globe2 } from 'lucide-react';

const SOURCE_LANGUAGE = 'en';
const GOOGLE_SCRIPT_ID = 'google-translate-script';
let translatorLoadPromise: Promise<void> | null = null;

const languages = [
  { label: 'English', shortLabel: 'EN', code: 'en' },
  { label: 'Svenska', shortLabel: 'SV', code: 'sv' },
  { label: 'Español', shortLabel: 'ES', code: 'es' },
  { label: 'Deutsch', shortLabel: 'DE', code: 'de' },
];

type LanguageSelectorProps = { onSelect?: () => void };

function loadGoogleTranslator() {
  if (document.querySelector('.goog-te-combo')) return Promise.resolve();
  if (translatorLoadPromise) return translatorLoadPromise;

  translatorLoadPromise = new Promise((resolve, reject) => {
    const script = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
    const googleWindow = window as typeof window & { google?: any; googleTranslateElementInit?: () => void };

    googleWindow.googleTranslateElementInit = () => {
      try {
        if (!googleWindow.google?.translate) throw new Error('Google Translate did not initialize.');
        new googleWindow.google.translate.TranslateElement(
          { pageLanguage: SOURCE_LANGUAGE, autoDisplay: false },
          'google_translate_element',
        );
        resolve();
      } catch (error) {
        translatorLoadPromise = null;
        reject(error);
      }
    };

    if (script) return;

    const newScript = document.createElement('script');
    newScript.id = GOOGLE_SCRIPT_ID;
    newScript.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    newScript.async = true;
    newScript.onerror = () => {
      translatorLoadPromise = null;
      reject(new Error('Google Translate could not be loaded.'));
    };
    document.head.appendChild(newScript);
  });

  return translatorLoadPromise;
}

function getSavedLanguage() {
  const cookie = document.cookie.split('; ').find((entry) => entry.startsWith('googtrans='));
  if (!cookie) return SOURCE_LANGUAGE;

  try {
    const target = decodeURIComponent(cookie.split('=').slice(1).join('='))
      .split('/')
      .filter(Boolean)[1];
    return languages.some((language) => language.code === target) ? target : SOURCE_LANGUAGE;
  } catch {
    return SOURCE_LANGUAGE;
  }
}

function clearTranslationCookies() {
  const hostname = window.location.hostname;
  const domains = [hostname, `.${hostname}`];
  const parts = hostname.split('.');
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join('.')}`);

  document.cookie = 'googtrans=; Max-Age=0; path=/';
  domains.forEach((domain) => {
    document.cookie = `googtrans=; Max-Age=0; path=/; domain=${domain}`;
  });
}

export const LanguageSelector = ({ onSelect }: LanguageSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState(SOURCE_LANGUAGE);
  const [status, setStatus] = useState<'idle' | 'error'>('idle');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const savedLanguage = getSavedLanguage();
    if (savedLanguage !== SOURCE_LANGUAGE) applyLanguage(savedLanguage);
    // A saved Google cookie needs the widget to be loaded before it can be applied.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  const applyLanguage = async (code: string) => {
    try {
      await loadGoogleTranslator();
      const select = document.querySelector<HTMLSelectElement>('.goog-te-combo');
      if (!select) throw new Error('Google Translate control is unavailable.');

      select.value = code;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      document.documentElement.lang = code;
      setActiveLanguage(code);
      setStatus('idle');
    } catch {
      setStatus('error');
    }
  };

  const selectLanguage = (code: string) => {
    setIsOpen(false);
    onSelect?.();

    if (code === SOURCE_LANGUAGE) {
      clearTranslationCookies();
      document.documentElement.lang = SOURCE_LANGUAGE;
      window.location.reload();
      return;
    }

    document.cookie = `googtrans=/${SOURCE_LANGUAGE}/${code}; path=/`;
    void applyLanguage(code);
  };

  const selected = languages.find((language) => language.code === activeLanguage) ?? languages[0];

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
        aria-label="Change language"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <Globe2 className="h-4 w-4" aria-hidden="true" />
        <span className="hidden lg:inline">{selected.label}</span>
        <span className="lg:hidden">{selected.shortLabel}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-[60] mt-2 min-w-44 overflow-hidden rounded-xl border border-gray-100 bg-white p-1.5 shadow-xl" role="menu">
          {languages.map((language) => (
            <button
              type="button"
              role="menuitemradio"
              aria-checked={activeLanguage === language.code}
              key={language.code}
              onClick={() => selectLanguage(language.code)}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors ${activeLanguage === language.code ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span>{language.label}</span>
              {activeLanguage === language.code && <Check className="h-4 w-4" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}

      {status === 'error' && <p className="sr-only" role="status">Translation is unavailable. Please try again later.</p>}
    </div>
  );
};
